import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  BarChart3, TrendingDown, Users, GraduationCap, Award,
  Download, ChevronRight, Sparkles,
} from "lucide-react";

const DEFAULT_COURSE = "course-mun-101";

/**
 * Sprint 3 — LMS Analytics Dashboard.
 *
 * Org-admin view of: enrollment buckets, module completion funnel,
 * per-lesson drop-off curve, school/committee cohorts. Includes a CSV
 * export button.
 */
export default function LMSAnalyticsPage() {
  const [courseId] = useState(DEFAULT_COURSE);
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [papers, setPapers] = useState(null);
  const [certs, setCerts] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, f, d, c, q, p, ce] = await Promise.all([
          api.get(`/lms-analytics/course/${courseId}/summary`),
          api.get(`/lms-analytics/course/${courseId}/funnel`),
          api.get(`/lms-analytics/course/${courseId}/dropoff`),
          api.get(`/lms-analytics/course/${courseId}/cohorts`),
          api.get(`/lms-analytics/quiz-performance`),
          api.get(`/lms-analytics/papers`),
          api.get(`/lms-analytics/certificates`),
        ]);
        setSummary(s.data);
        setFunnel(f.data);
        setDropoff(d.data);
        setCohorts(c.data);
        setQuiz(q.data);
        setPapers(p.data);
        setCerts(ce.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Analytics unavailable");
      }
    };
    load();
  }, [courseId]);

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/lms-analytics/export?course_id=${courseId}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lms-${courseId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not export");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="lms-analytics-root">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> LMS Analytics
            </p>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight text-[#0A192F]">
              {summary?.course?.title || "Course analytics"}
            </h1>
          </div>
          <button
            type="button"
            onClick={downloadCsv}
            className="self-start md:self-auto px-4 py-2 rounded-md bg-[#0F4C81] hover:bg-[#0A3860] text-white text-sm flex items-center gap-2"
            data-testid="lms-analytics-export"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <KpiCard label="Enrolled" value={summary.enrolled} icon={Users} testid="kpi-enrolled" />
            <KpiCard label="In progress" value={summary.in_progress} icon={TrendingDown} testid="kpi-inprogress" />
            <KpiCard label="Completed" value={summary.completed} icon={GraduationCap} tone="success" testid="kpi-completed" />
            <KpiCard label="Avg watch %" value={`${summary.avg_watch_percent}%`} icon={BarChart3} testid="kpi-avg-watch" />
            <KpiCard label="Overdue" value={summary.overdue} icon={TrendingDown} tone="warn" testid="kpi-overdue" />
            <KpiCard label="At risk" value={summary.at_risk} icon={TrendingDown} tone="danger" testid="kpi-atrisk" />
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          <Card title="Module completion funnel" testid="card-funnel">
            {funnel ? (
              <div className="space-y-3">
                {funnel.funnel.map((m, i) => {
                  const max = Math.max(...funnel.funnel.map((x) => x.completed_delegates), 1);
                  const pct = Math.round((m.completed_delegates / max) * 100);
                  return (
                    <div key={m.module_id} data-testid={`funnel-row-${m.module_id}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-[#0A192F]">{m.title}</span>
                        <span className="text-slate-500">{m.completed_delegates} delegates</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#0F4C81] to-[#D4AF37] transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Skeleton />
            )}
          </Card>

          <Card title="Per-lesson drop-off" testid="card-dropoff">
            {dropoff ? (
              <div className="space-y-2 max-h-[360px] overflow-y-auto">
                {dropoff.lessons.map((ls) => (
                  <div key={ls.lesson_id} className="flex items-center gap-3" data-testid={`dropoff-${ls.lesson_id}`}>
                    <span className="text-xs text-slate-600 w-44 truncate">{ls.title}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full ${ls.avg_watch_percent >= 75 ? "bg-emerald-500" : ls.avg_watch_percent >= 40 ? "bg-amber-500" : "bg-rose-500"}`}
                        style={{ width: `${Math.min(ls.avg_watch_percent, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-16 text-right">
                      {ls.avg_watch_percent}%
                    </span>
                  </div>
                ))}
              </div>
            ) : <Skeleton />}
          </Card>

          <Card title="School cohorts" testid="card-schools">
            {cohorts && cohorts.schools.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left py-1">School</th>
                    <th className="text-right py-1">Enrolled</th>
                    <th className="text-right py-1">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.schools.map((s) => (
                    <tr key={s.name} className="border-t border-slate-100" data-testid={`school-row-${s.name}`}>
                      <td className="py-1.5">{s.name}</td>
                      <td className="text-right">{s.enrolled}</td>
                      <td className="text-right font-semibold text-[#0F4C81]">{s.completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState text="No school-level data yet — delegates will appear here as they join the course." />
            )}
          </Card>

          <Card title="Committee cohorts" testid="card-committees">
            {cohorts && cohorts.committees.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left py-1">Committee</th>
                    <th className="text-right py-1">Enrolled</th>
                    <th className="text-right py-1">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.committees.map((c) => (
                    <tr key={c.name} className="border-t border-slate-100" data-testid={`committee-row-${c.name}`}>
                      <td className="py-1.5">{c.name}</td>
                      <td className="text-right">{c.enrolled}</td>
                      <td className="text-right font-semibold text-[#0F4C81]">{c.completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState text="No committee allocations against this course yet." />
            )}
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <MiniStat label="Quiz attempts" sub={quiz?.overall ? `Pass rate ${quiz.overall.pass_rate}%` : "No attempts yet"} value={quiz?.overall?.attempts ?? 0} icon={GraduationCap} testid="ministat-quiz" />
          <MiniStat label="Position papers" sub={`${papers?.reviewed ?? 0} reviewed`} value={papers?.submitted ?? 0} icon={Award} testid="ministat-papers" />
          <MiniStat label="Certificates issued" sub={`${certs?.eligible ?? 0} eligible`} value={certs?.issued ?? 0} icon={Award} testid="ministat-certs" />
        </div>
      </div>
    </DashboardLayout>
  );
}

function KpiCard({ label, value, icon: Icon, tone, testid }) {
  const ring = tone === "success"
    ? "border-emerald-200 bg-emerald-50"
    : tone === "warn"
    ? "border-amber-200 bg-amber-50"
    : tone === "danger"
    ? "border-rose-200 bg-rose-50"
    : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border ${ring} p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <Icon className="h-3.5 w-3.5 text-slate-400" />
      </div>
      <p className="mt-1.5 text-2xl font-bold text-[#0A192F]">{value}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold mb-3">{title}</p>
      {children}
    </div>
  );
}

function MiniStat({ label, sub, value, icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg bg-[#0A192F]/5 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#0F4C81]" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <p className="mt-1 text-2xl font-bold text-[#0A192F]">{value}</p>
        <p className="text-xs text-slate-500">{sub}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-sm border border-dashed border-slate-200 bg-white">
      <div className="h-7 w-7 rounded-sm bg-brand-navy-dark text-brand-gold flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none">
          <path d="M12 3v2M3 12h2M12 21v-2M21 12h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <p className="text-xs text-slate-500 italic">{text}</p>
    </div>
  );
}

function Skeleton() {
  return <div className="h-32 rounded-md bg-slate-100 animate-pulse" />;
}
